// frontend/src/components/onboarding/customer-links-dialog.tsx
// Per-customer onboarding links modal (Customers table). Links are split into
// WhatsApp / Instagram tabs (a tab is disabled when that channel has none), and
// each tab holds a searchable, height-capped table so a customer with many
// links stays manageable. Every link is re-displayable (copy) plus Regenerate
// and Revoke. With no links at all, the create dialog opens directly.
// Title Case button labels; no em-dashes.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Facebook, Instagram, Loader2, MessageCircle, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useFetch } from '@/hooks/use-fetch';
import { parseApiError } from '@/lib/api-error';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';
import { CopyButton } from './copy-button';
import { CreateLinkDialog, type EditableLink } from './create-link-dialog';

type ChannelType = 'whatsapp' | 'instagram' | 'facebook';

interface CustomerRef {
  publicId: string;
  name: string;
}

/** Row of GET /org/onboarding-links (the fields this dialog uses). */
interface LinkRow {
  publicId: string;
  label: string;
  channelType: ChannelType;
  status: 'active' | 'revoked';
  /** Standing connect URL (always present for links minted after the fix). */
  url: string | null;
  externalId: string | null;
  destinationWebhookUrl: string | null;
  destinationVerifyTokenOverride: string | null;
  successRedirectUrl: string | null;
  failureRedirectUrl: string | null;
  targetWorkspace: { publicId: string } | null;
}

const CHANNELS: { type: ChannelType; label: string; Icon: typeof MessageCircle }[] = [
  { type: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle },
  { type: 'instagram', label: 'Instagram', Icon: Instagram },
  { type: 'facebook', label: 'Facebook', Icon: Facebook },
];

/** A list row → the shape the Edit dialog prefills from. */
function toEditable(l: LinkRow): EditableLink {
  return {
    publicId: l.publicId,
    channelType: l.channelType,
    label: l.label,
    externalId: l.externalId,
    destinationWebhookUrl: l.destinationWebhookUrl,
    destinationVerifyTokenOverride: l.destinationVerifyTokenOverride,
    successRedirectUrl: l.successRedirectUrl,
    failureRedirectUrl: l.failureRedirectUrl,
  };
}

interface CustomerLinksDialogProps {
  /** The customer whose links to manage; null = closed. */
  customer: CustomerRef | null;
  onClose: () => void;
}

export function CustomerLinksDialog({ customer, onClose }: CustomerLinksDialogProps) {
  const fetchWithAuth = useFetch();
  const [links, setLinks] = useState<LinkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyPublicId, setBusyPublicId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editLink, setEditLink] = useState<LinkRow | null>(null);
  const [tab, setTab] = useState<ChannelType>('whatsapp');
  const [search, setSearch] = useState('');

  const customerId = customer?.publicId ?? null;

  const loadLinks = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await fetchWithAuth('/org/onboarding-links');
      if (res.ok) {
        const data = (await res.json()) as { links: LinkRow[] };
        const mine = data.links.filter(
          (l) => l.status === 'active' && l.targetWorkspace?.publicId === customerId,
        );
        setLinks(mine);
        // No links yet → skip the empty list and open the create dialog directly
        // (no two-step "list then Generate" dance).
        if (mine.length === 0) setCreateOpen(true);
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to load links').userMessage);
      }
    } catch {
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  }, [customerId, fetchWithAuth]);

  useEffect(() => {
    void loadLinks();
  }, [loadLinks]);

  const byChannel = useMemo(() => {
    const map: Record<ChannelType, LinkRow[]> = { whatsapp: [], instagram: [], facebook: [] };
    for (const l of links) map[l.channelType].push(l);
    return map;
  }, [links]);

  // Keep the active tab on a channel that actually has links (so a disabled tab
  // is never the selected one after a load/revoke).
  useEffect(() => {
    setTab((prev) => {
      if (byChannel[prev].length > 0) return prev;
      const withLinks = CHANNELS.find((c) => byChannel[c.type].length > 0);
      return withLinks ? withLinks.type : prev;
    });
  }, [byChannel]);

  // Reset the search box each time the modal opens for a customer.
  useEffect(() => {
    setSearch('');
  }, [customerId]);

  async function handleRegenerate(link: LinkRow) {
    setBusyPublicId(link.publicId);
    try {
      const res = await fetchWithAuth(`/org/onboarding-links/${link.publicId}/regenerate`, {
        method: 'POST',
      });
      if (res.ok) {
        toast.success('Link regenerated');
        await loadLinks();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to regenerate link').userMessage);
      }
    } catch {
      toast.error('Failed to regenerate link');
    } finally {
      setBusyPublicId(null);
    }
  }

  async function handleRevoke(link: LinkRow) {
    setBusyPublicId(link.publicId);
    try {
      const res = await fetchWithAuth(`/org/onboarding-links/${link.publicId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        toast.success('Link revoked');
        await loadLinks();
      } else {
        const body = await res.json().catch(() => null);
        toast.error(parseApiError(res.status, body, 'Failed to revoke link').userMessage);
      }
    } catch {
      toast.error('Failed to revoke link');
    } finally {
      setBusyPublicId(null);
    }
  }

  function renderLinkTable(rows: LinkRow[]) {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter((l) => `${l.label} ${l.url ?? ''}`.toLowerCase().includes(q))
      : rows;
    return (
      <div className="max-h-72 overflow-y-auto rounded-md border">
        <Table className="table-fixed">
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '48%' }} />
            <col style={{ width: '22%' }} />
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-card">
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Link</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((link) => (
              <TableRow key={link.publicId}>
                <TableCell className="truncate font-medium">{link.label}</TableCell>
                <TableCell>
                  <div className="flex min-w-0 items-center gap-1">
                    <span
                      className="min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground"
                      title={link.url ?? ''}
                    >
                      {link.url ? midEllipsisUrl(link.url) : null}
                    </span>
                    <CopyButton value={link.url ?? ''} label={`Connect link for ${link.label}`} />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Edit ${link.label}`}
                      onClick={() => setEditLink(link)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={busyPublicId === link.publicId}
                      aria-label={`Regenerate ${link.label}`}
                      onClick={() => void handleRegenerate(link)}
                    >
                      <RefreshCw className="size-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      disabled={busyPublicId === link.publicId}
                      aria-label={`Revoke ${link.label}`}
                      onClick={() => void handleRevoke(link)}
                    >
                      {busyPublicId === link.publicId ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="size-3.5 text-destructive" />
                      )}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-muted-foreground">
                  No links match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <>
      {/* The tabbed list shows only when the customer actually has links. With
          none, loadLinks opens the create dialog directly (single step). */}
      <Dialog
        open={customer !== null && !loading && links.length > 0}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Onboarding links for {customer?.name}</DialogTitle>
            <DialogDescription>
              Share a link with this customer to connect their channel. Links stay
              copyable here anytime.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={(v) => setTab(v as ChannelType)}>
            <TabsList className="w-full">
              {CHANNELS.map(({ type, label, Icon }) => (
                <TabsTrigger key={type} value={type} disabled={byChannel[type].length === 0}>
                  <Icon className="size-3.5" />
                  {label}
                  <span className="text-muted-foreground">({byChannel[type].length})</span>
                </TabsTrigger>
              ))}
            </TabsList>

            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search links..."
              aria-label="Search links"
              className="h-8"
            />

            {CHANNELS.map(({ type }) => (
              <TabsContent key={type} value={type}>
                {renderLinkTable(byChannel[type])}
              </TabsContent>
            ))}
          </Tabs>

          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)}>
              <Plus className="size-3.5" />
              New Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CreateLinkDialog
        open={createOpen || editLink !== null}
        presetCustomer={customer}
        editLink={editLink ? toEditable(editLink) : null}
        onOpenChange={(open) => {
          if (!open) {
            setCreateOpen(false);
            setEditLink(null);
            // Dismissing CREATE with still no links ends the whole flow (there is
            // no list modal to fall back to for a customer with zero links).
            if (!editLink && links.length === 0) onClose();
          }
        }}
        onSaved={async () => {
          await loadLinks();
        }}
      />
    </>
  );
}
